import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import PublicHeader, { PublicFooter } from "@/components/PublicHeader";
import { MapPin, Users, Handshake, HandCoins, BadgeCheck, MessageCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PincodeHub() {
  const { pincode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Pincode ${pincode} — Meet neighbours · MyLiveFriend`;
    setLoading(true);
    axios.get(`${BACKEND}/api/public/pincode/${pincode}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ pincode, found: false }))
      .finally(() => setLoading(false));
  }, [pincode]);

  // JSON-LD structured data for local Place
  const jsonLd = data?.found ? {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": `MyLiveFriend neighbours in ${data.city} · PIN ${data.pincode}`,
    "address": {
      "@type": "PostalAddress",
      "postalCode": data.pincode,
      "addressLocality": data.city,
      "addressRegion": data.state,
      "addressCountry": "IN",
    },
    "description": `${data.total} MyLiveFriend neighbours in pincode ${data.pincode}, ${data.city}. ${data.available_count} available for rental, ${data.looking_count} looking to rent.`,
  } : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <nav className="text-xs text-slate-500 flex items-center gap-1 mb-6">
          <Link to="/" className="hover:text-teal-700">Home</Link>
          <span>›</span>
          <span>Pincode</span>
          <span>›</span>
          <span className="font-mono font-semibold text-slate-700">{pincode}</span>
        </nav>

        {loading ? (
          <div className="animate-pulse bg-white rounded-2xl border border-slate-200 h-48"></div>
        ) : !data?.found ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
            <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Pincode {pincode}</h1>
            <p className="text-slate-500 mt-2">No neighbours have joined MyLiveFriend from this pincode yet.</p>
            <Link to="/" className="inline-block mt-5 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 py-2.5 font-semibold text-sm">
              Be the first — sign in
            </Link>
          </div>
        ) : (
          <>
            <header className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-teal-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                    Pincode <span className="font-mono text-teal-700">{data.pincode}</span>
                  </h1>
                  <p className="text-slate-600 mt-1">
                    {data.city}{data.state ? `, ${data.state}` : ""} · India
                  </p>
                </div>
                <Link to="/" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-5 py-2.5 font-semibold text-sm">
                  Join this pincode
                </Link>
              </div>
            </header>

            <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pincode-stats">
              <Stat Icon={Users} value={data.total} label="Neighbours" color="teal" testid="stat-total" />
              <Stat Icon={Handshake} value={data.available_count} label="Available for Rental" color="emerald" testid="stat-available" />
              <Stat Icon={HandCoins} value={data.looking_count} label="Looking to Rent" color="indigo" testid="stat-looking" />
              <Stat Icon={BadgeCheck} value={data.verified_count} label="Verified Pro" color="amber" testid="stat-verified" />
            </section>

            {data.messages_week > 0 && (
              <section className="mt-6 rounded-2xl bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400 p-5 text-white">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-6 h-6" />
                  <div>
                    <div className="font-heading text-xl font-extrabold">{data.messages_week} messages this week</div>
                    <div className="text-sm opacity-90">{data.pincode} is active right now. Join the conversation.</div>
                  </div>
                </div>
              </section>
            )}

            {data.areas?.length > 0 && (
              <section className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
                <h2 className="font-heading font-bold text-lg text-slate-900">Localities in this pincode</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.areas.map((a) => (
                    <span key={a} className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-100 text-slate-700">{a}</span>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-6 rounded-2xl bg-slate-900 text-white p-6 sm:p-8 text-center">
              <h2 className="font-heading text-2xl font-bold">See who's in {data.pincode}</h2>
              <p className="text-slate-300 mt-2 text-sm">Profiles are kept private. Sign in with Google and subscribe to view full profiles and chat.</p>
              <Link to="/" className="inline-block mt-5 bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6 py-3 font-semibold text-sm">
                Sign in with Google
              </Link>
            </section>

            {jsonLd && (
              <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
            )}
          </>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

function Stat({ Icon, value, label, color, testid }) {
  const map = {
    teal: "bg-teal-50 text-teal-700 border-teal-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 ${map[color]}`}>
      <Icon className="w-5 h-5 opacity-80" />
      <div className="mt-2 font-heading text-3xl font-extrabold">{value}</div>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80 mt-1 line-clamp-1">{label}</div>
    </div>
  );
}
